import { zodResolver } from '@hookform/resolvers/zod';
import type { AdminSelf } from '@saay/api-client';
import { isApiError } from '@saay/api-client';
import {
  formatLocalDate,
  formatRatePercent,
  formatTimestamp,
  formatToman,
  normalizeCodeInput,
  statusLabel,
  toPersianDigits,
} from '@saay/formatting';
import {
  AccordionPanel,
  applyFieldErrors,
  Banner,
  Button,
  ChipRow,
  CommandFeedback,
  Dialog,
  DiffList,
  type DiffRow,
  EmptyState,
  ErrorState,
  FieldErrorList,
  FilterChip,
  Icon,
  List,
  ListItem,
  LoadMore,
  MoneyField,
  MultiSelectCombobox,
  NativeSelect,
  NumberField,
  PageHeader,
  PercentField,
  Section,
  SegmentedButtons,
  SideSheet,
  Skeleton,
  StatusChip,
  Switch,
  TextArea,
  TextField,
  useToast,
} from '../kit.js';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useState } from 'react';
import {
  Controller,
  type FieldValues,
  type Path,
  useFieldArray,
  useForm,
  type UseFormReturn,
} from 'react-hook-form';
import { Link, useSearchParams } from 'react-router';
import { z } from 'zod';

import type {
  ApprovalActionType,
  ApprovalPolicyAction,
  ConfirmationPolicyVersionCreate,
  DelinquencyPolicyVersion,
  DelinquencyPolicyVersionCreate,
  InstallmentBoundsPolicyVersionCreate,
  LateFeeModel,
  LateFeePolicyVersion,
  PolicyVersion,
  PolicyVersionCreate,
} from '../api.js';
import { type CommandOutcome, useCommand } from '@saay/web-app';
import { AdminSolarDateField } from '../components/admin-date-field.js';
import { useSubmitBlocked } from '../components/form.js';
import { QueryState, RequireArea, usePageTitle } from '../components/guard.js';
import { OnDemandCopy } from '../components/on-demand-copy.js';
import { GUARANTEE_POLICY_FULL_FA, GUARANTEE_POLICY_SHORT_FA } from '../ux-copy.js';
import { formatDays, formatSeconds } from '../format.js';
import {
  canManagePolicy,
  canReadNumberingPolicy,
  canReadPolicy,
  canReadWalletPolicy,
  NUMBERING_POLICY_TYPE,
  type NumberingPolicyType,
  POLICY_TYPES,
  type StagePolicyType,
  WALLET_POLICY_TYPES,
  type WalletPolicyType,
} from '../permissions.js';
import { listStatus, setupKeys, useCurrentPolicy, usePolicyVersions } from '../queries.js';
import { REASON_MAX, tehranToday } from '../validation.js';
import { Notice } from '@saay/blocks';
import { buttonVariants } from '@saay/shadcn/components/button';
import { cn } from '@saay/shadcn/lib/utils';
import { CreditCardIcon } from 'lucide-react';
import { physicalCardsPath } from '../nav.js';
import { OUTLINE_PILL } from '../components/surface-section.js';
import { policyReasonText, WalletPolicyPanel, walletPolicyRows } from './wallet-policies.js';

// ---------------------------------------------------------------------------------------------
// Values (read)

function lateFeeText(lateFee: DelinquencyPolicyVersion['lateFee']): string {
  const model = statusLabel('lateFeeModel', lateFee.model);
  switch (lateFee.model) {
    case 'FIXED_AMOUNT':
      return lateFee.fixedAmountIrr === null
        ? model
        : `${model}: ${formatToman(lateFee.fixedAmountIrr)}`;
    case 'FIXED_PERCENTAGE_OF_INSTALLMENT':
      return lateFee.fixedPercentage === null
        ? model
        : `${model}: ${formatRatePercent(lateFee.fixedPercentage, { invalid: 'raw' })}`;
    case 'DAILY_WITH_CAP':
      return `${model}: روزانه ${
        lateFee.dailyAmountIrr === null ? '—' : formatToman(lateFee.dailyAmountIrr)
      }، سقف ${lateFee.dailyCapIrr === null ? '—' : formatToman(lateFee.dailyCapIrr)}`;
    default:
      return model;
  }
}

/**
 * AC §18.2.2: the "always" action types. Their rows are sent `enabled: true` with no threshold and
 * are not editable; every other type is a "policy" action with an optional amount threshold.
 */
export const ALWAYS_APPROVAL_ACTIONS: ReadonlySet<ApprovalActionType> = new Set([
  'MERCHANT_CAPACITY_CHANGE',
  'MERCHANT_FEE_CHANGE',
  'PAYOUT_ACCOUNT_CHANGE',
  'WRITE_OFF',
  'MERCHANT_RECOVERY_CLAIM',
  'MERCHANT_RECOVERY_WAIVER',
  'FINANCIAL_ADJUSTMENT',
  'PLATFORM_ROLE_GRANT',
]);

/** Every approval action type, once each (AC §5, §18.10: 12 rows). */
export const APPROVAL_ACTION_TYPES: readonly ApprovalActionType[] = [
  'BUYER_CREDIT_CHANGE',
  'CREDIT_ALLOCATION_ADJUSTMENT',
  'MERCHANT_CAPACITY_CHANGE',
  'MERCHANT_FEE_CHANGE',
  'PAYOUT_ACCOUNT_CHANGE',
  'WRITE_OFF',
  'CREDIT_PROGRAM_ACTIVATION',
  'CREDIT_PROGRAM_TARGETING_RUN',
  'MERCHANT_RECOVERY_CLAIM',
  'MERCHANT_RECOVERY_WAIVER',
  'FINANCIAL_ADJUSTMENT',
  'PLATFORM_ROLE_GRANT',
];

function approvalActionText(action: ApprovalPolicyAction): string {
  const hours = `مهلت تصمیم ${toPersianDigits(action.expiresAfterHours)} ساعت`;
  if (ALWAYS_APPROVAL_ACTIONS.has(action.actionType)) {
    return `همیشه با تأیید دوم · ${hours}`;
  }
  if (!action.enabled) {
    return 'بدون تأیید دوم';
  }
  return action.thresholdIrr === null || action.thresholdIrr === 0
    ? `همیشه با تأیید دوم · ${hours}`
    : `از ${formatToman(action.thresholdIrr)} به بالا با تأیید دوم · ${hours}`;
}

type GuaranteeAgreementRef = Extract<
  PolicyVersion,
  { policyType: 'guarantee' }
>['currentAgreement'];

/** The agreement in force when a guarantee version starts (AC §18.10); the body is buyer-facing only. */
function agreementText(agreement: GuaranteeAgreementRef): string {
  return agreement === null
    ? 'منتشر نشده'
    : `${agreement.titleFa} · نسخه ${toPersianDigits(agreement.version)} · از ${formatTimestamp(agreement.publishedAt, { withTime: false })}`;
}

/** The type's values as label/value rows (AC §17.5.3, §18.10). */
export function policyValueRows(version: PolicyVersion): [string, ReactNode][] {
  switch (version.policyType) {
    case 'installment-bounds':
      return [
        ['حداقل فاصله اقساط', formatDays(version.minIntervalDays)],
        ['حداکثر فاصله اقساط', formatDays(version.maxIntervalDays)],
        ['حداکثر مدت کل', formatDays(version.maxTotalDurationDays)],
        ['حداقل فاصله تا پایان ماه', formatDays(version.endOfSaleMonthMinLeadDays)],
      ];
    case 'confirmation':
      return [
        ['مهلت رزرو', formatSeconds(version.reservationTtlSeconds)],
        ['اعتبار کد پیامکی', formatSeconds(version.smsCodeTtlSeconds)],
        ['اعتبار QR', formatSeconds(version.qrTtlSeconds)],
        ['مهلت تأیید مرجوعی', formatSeconds(version.returnRequestTtlSeconds)],
      ];
    case 'delinquency':
      return [
        ['مهلت پرداخت پس از سررسید', formatDays(version.graceDays)],
        ['جریمه دیرکرد', lateFeeText(version.lateFee)],
        ['محدودسازی خریدار معوق', version.restrictBuyerOnDelinquency ? 'بله' : 'خیر'],
        ['نامزد سوخت پس از', formatDays(version.writeOffCandidateDays)],
      ];
    case 'late-fee':
      return [
        [
          'مدل‌های مجاز',
          version.enabledModels.map((model) => statusLabel('lateFeeModel', model)).join('، '),
        ],
        ['مرجع تأیید حقوقی', version.legalSignoffReference ?? '—'],
      ];
    case 'buyer-provisioning':
      return [
        ['اعتبار پایه پیش‌فرض', formatToman(version.defaultInitialCreditIrr)],
        [
          'مدارک شناسایی پذیرفته',
          version.acceptedIdentityDocumentTypes
            .map((code) => {
              const label = statusLabel('identityDocumentType', code);
              return label === 'نامشخص' ? code : label;
            })
            .join('، '),
        ],
        ['پیامک دعوت به‌صورت پیش‌فرض', version.sendLoginInvitationByDefault ? 'بله' : 'خیر'],
      ];
    case 'credit':
      return [
        ['سقف اعتبار پایه', formatToman(version.maxBaseCreditIrr)],
        ['سقف کل اعتبار هر خریدار', formatToman(version.maxTotalCreditPerBuyerIrr)],
      ];
    case 'approval':
      return version.actions.map((action) => [
        statusLabel('approvalActionType', action.actionType),
        approvalActionText(action),
      ]);
    case 'guarantee':
      return [
        ['تعداد ضامن مستقیم لازم', toPersianDigits(version.requiredDirectGuarantorCount)],
        ['حداکثر ضمانت هر ضامن', toPersianDigits(version.maxGuaranteedBuyersPerGuarantor)],
        ['اعتبار کد معرف', formatDays(version.referralCodeTtlDays)],
        [
          'محدودسازی ضامنان لایه اول',
          version.restrictLayer1GuarantorsOnDelinquency ? 'بله' : 'خیر',
        ],
        ['توافق‌نامهٔ ضمانت', agreementText(version.currentAgreement)],
      ];
    case 'payment-fee':
      return [
        ...version.brackets.map((bracket): [string, ReactNode] => [
          bracket.upToIrr === null ? 'بازهٔ بدون سقف' : `تا ${formatToman(bracket.upToIrr)}`,
          formatRatePercent(bracket.rate, { invalid: 'raw' }),
        ]),
        ['حداقل کارمزد', formatToman(version.minimumFeeIrr)],
      ];
    case 'cash-sale-fee':
      return [
        ['کارمزد پیش‌فرض فروش نقدی', formatRatePercent(version.defaultRate, { invalid: 'raw' })],
      ];
    case 'wallet':
    case 'wallet-top-up-fee':
    case 'wallet-credit':
      return walletPolicyRows(version);
    default:
      return [];
  }
}

function policyDiffRows(current: PolicyVersion, next: PolicyVersion): readonly DiffRow[] {
  const before = new Map(policyValueRows(current));
  const after = new Map(policyValueRows(next));
  const keys = new Set([...before.keys(), ...after.keys()]);
  const rows: DiffRow[] = [];
  for (const key of [...keys].sort((a, b) => a.localeCompare(b, 'fa'))) {
    const previous = before.get(key);
    const nextValue = after.get(key);
    const changed = String(previous) !== String(nextValue);
    if (!changed) continue;
    rows.push({
      changed: true,
      key,
      label: key,
      next: nextValue ?? '—',
      previous: previous ?? '—',
    });
  }
  return rows;
}

function CurrentVersion({
  manage,
  onEdit,
  scheduled,
  version,
}: {
  readonly manage: boolean;
  readonly onEdit: () => void;
  readonly scheduled: PolicyVersion | undefined;
  readonly version: PolicyVersion;
}) {
  return (
    <>
      <div className="admin-actions admin-actions--end">
        {manage ? (
          <Button onClick={onEdit} size="sm" variant="secondary">
            ویرایش
          </Button>
        ) : null}
      </div>
      <p>
        {`نسخه ${toPersianDigits(version.version)} — از ${formatLocalDate(version.effectiveFrom, 'long')} `}
        <StatusChip enumName="policyVersionStatus" value={version.status} />
      </p>
      <dl className="saay-facts">
        {policyValueRows(version).map(([term, value]) => (
          <div className="admin-fact" key={term}>
            <dt>{term}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      {scheduled === undefined ? null : (
        <div className="admin-policy-scheduled">
          <p>
            <strong>{`نسخهٔ بعدی از ${formatLocalDate(scheduled.effectiveFrom, 'long')}`}</strong>
          </p>
          <DiffList
            items={policyDiffRows(version, scheduled)}
            label="تغییرات نسخهٔ زمان‌بندی‌شده"
          />
        </div>
      )}
    </>
  );
}

function History({ versions }: { readonly versions: readonly PolicyVersion[] }) {
  const sorted = [...versions].sort((left, right) => right.version - left.version);
  const items = sorted.map((row, index) => {
    const previous = sorted[index + 1];
    const diff = previous === undefined ? [] : policyDiffRows(previous, row);
    return {
      content:
        diff.length === 0 ? (
          <p className="admin-muted">بدون تغییر نسبت به نسخهٔ قبل</p>
        ) : (
          <DiffList items={diff} label={`تغییرات نسخه ${toPersianDigits(row.version)}`} />
        ),
      key: row.id,
      summary: policyReasonText(row.reason),
      title: (
        <>
          {`نسخه ${toPersianDigits(row.version)} · ${formatLocalDate(row.effectiveFrom)} `}
          <StatusChip enumName="policyVersionStatus" value={row.status} />
        </>
      ),
    };
  });
  return <AccordionPanel items={items} label="سابقه نسخه‌ها" />;
}

// ---------------------------------------------------------------------------------------------
// New version (write): installment-bounds, confirmation, delinquency (AC §17.5.3)

const fa = (value: number) => toPersianDigits(value);

function intField(min: number, max: number) {
  return z
    .string()
    .transform(normalizeCodeInput)
    .refine((value) => /^\d+$/.test(value), 'یک عدد صحیح وارد کنید.')
    .transform(Number)
    .refine((value) => value >= min && value <= max, `بین ${fa(min)} تا ${fa(max)}.`);
}

const meta = {
  effectiveFrom: z.string().min(1, 'تاریخ شروع را وارد کنید.'),
  reason: z
    .string()
    .trim()
    .min(1, 'دلیل الزامی است.')
    .max(REASON_MAX, `حداکثر ${toPersianDigits(REASON_MAX)} کاراکتر.`),
};

const boundsSchema = z
  .object({
    ...meta,
    endOfSaleMonthMinLeadDays: intField(0, 29),
    maxIntervalDays: intField(1, 366),
    maxTotalDurationDays: intField(1, 3660),
    minIntervalDays: intField(1, 366),
  })
  .superRefine((value, context) => {
    if (value.maxIntervalDays < value.minIntervalDays) {
      context.addIssue({
        code: 'custom',
        message: 'حداکثر فاصله نباید از حداقل کمتر باشد.',
        path: ['maxIntervalDays'],
      });
    }
    if (value.maxTotalDurationDays < value.maxIntervalDays) {
      context.addIssue({
        code: 'custom',
        message: 'مدت کل نباید از حداکثر فاصله کمتر باشد.',
        path: ['maxTotalDurationDays'],
      });
    }
  });

const confirmationSchema = z
  .object({
    ...meta,
    qrTtlSeconds: intField(30, 600),
    reservationTtlSeconds: intField(60, 1800),
    returnRequestTtlSeconds: intField(60, 604_800),
    smsCodeTtlSeconds: intField(60, 1800),
  })
  .superRefine((value, context) => {
    if (value.smsCodeTtlSeconds > value.reservationTtlSeconds) {
      context.addIssue({
        code: 'custom',
        message: 'اعتبار کد پیامکی نباید از مهلت رزرو بیشتر باشد.',
        path: ['smsCodeTtlSeconds'],
      });
    }
  });

const delinquencySchema = z
  .object({
    ...meta,
    graceDays: intField(0, 60),
    lateFee: z.object({
      dailyAmountIrr: z.number().int().nullable(),
      dailyCapIrr: z.number().int().nullable(),
      fixedAmountIrr: z.number().int().nullable(),
      fixedPercentage: z.string(),
      model: z.enum(['NONE', 'FIXED_AMOUNT', 'FIXED_PERCENTAGE_OF_INSTALLMENT', 'DAILY_WITH_CAP']),
    }),
    restrictBuyerOnDelinquency: z.boolean(),
    writeOffCandidateDays: intField(1, 3650),
  })
  .superRefine((value, context) => {
    const need = (field: 'dailyAmountIrr' | 'dailyCapIrr' | 'fixedAmountIrr') => {
      const amount = value.lateFee[field];
      if (amount === null || amount < 10) {
        context.addIssue({
          code: 'custom',
          message: 'مبلغ را وارد کنید.',
          path: ['lateFee', field],
        });
      }
    };
    if (value.lateFee.model === 'FIXED_AMOUNT') need('fixedAmountIrr');
    if (value.lateFee.model === 'DAILY_WITH_CAP') {
      need('dailyAmountIrr');
      need('dailyCapIrr');
    }
    if (
      value.lateFee.model === 'FIXED_PERCENTAGE_OF_INSTALLMENT' &&
      !/^0\.\d{1,6}$/.test(normalizeCodeInput(value.lateFee.fixedPercentage))
    ) {
      context.addIssue({
        code: 'custom',
        message: 'نرخ را به شکل اعشاری مانند 0.020000 وارد کنید.',
        path: ['lateFee', 'fixedPercentage'],
      });
    }
  });

const LATE_FEE_MODELS: readonly LateFeeModel[] = [
  'NONE',
  'FIXED_AMOUNT',
  'FIXED_PERCENTAGE_OF_INSTALLMENT',
  'DAILY_WITH_CAP',
];
/** D-07: a percentage or daily model needs the legal sign-off reference. */
const SIGNOFF_MODELS: ReadonlySet<LateFeeModel> = new Set([
  'FIXED_PERCENTAGE_OF_INSTALLMENT',
  'DAILY_WITH_CAP',
]);

const lateFeeSchema = z
  .object({
    ...meta,
    enabledModels: z
      .array(z.enum(['NONE', 'FIXED_AMOUNT', 'FIXED_PERCENTAGE_OF_INSTALLMENT', 'DAILY_WITH_CAP']))
      .min(1, 'دست‌کم یک مدل را انتخاب کنید.'),
    legalSignoffReference: z.string().trim().max(200, 'حداکثر ۲۰۰ کاراکتر.'),
  })
  .superRefine((value, context) => {
    if (!value.enabledModels.includes('NONE')) {
      context.addIssue({
        code: 'custom',
        message: 'مدل «بدون جریمه» همیشه فعال است.',
        path: ['enabledModels'],
      });
    }
    if (
      value.enabledModels.some((model) => SIGNOFF_MODELS.has(model)) &&
      value.legalSignoffReference === ''
    ) {
      context.addIssue({
        code: 'custom',
        message: 'برای جریمهٔ درصدی یا روزانه، مرجع تأیید حقوقی لازم است.',
        path: ['legalSignoffReference'],
      });
    }
  });

const requiredRials = (message: string) =>
  z
    .number()
    .int()
    .min(0)
    .nullable()
    .refine((value) => value !== null, message);

const creditSchema = z
  .object({
    ...meta,
    maxBaseCreditIrr: requiredRials('سقف اعتبار پایه را وارد کنید.'),
    maxTotalCreditPerBuyerIrr: requiredRials('سقف کل اعتبار را وارد کنید.'),
  })
  .superRefine((value, context) => {
    if (
      value.maxBaseCreditIrr !== null &&
      value.maxTotalCreditPerBuyerIrr !== null &&
      value.maxTotalCreditPerBuyerIrr < value.maxBaseCreditIrr
    ) {
      context.addIssue({
        code: 'custom',
        message: 'سقف کل نباید از سقف اعتبار پایه کمتر باشد.',
        path: ['maxTotalCreditPerBuyerIrr'],
      });
    }
  });

const guaranteeSchema = z
  .object({
    ...meta,
    // AC §18.10: an optional new agreement version, plain text (max 200 / 20,000 characters).
    agreement: z.object({ bodyFa: z.string(), titleFa: z.string() }),
    maxGuaranteedBuyersPerGuarantor: intField(1, 100),
    publishAgreement: z.boolean(),
    referralCodeTtlDays: intField(1, 90),
    requiredDirectGuarantorCount: intField(0, 5),
    restrictLayer1GuarantorsOnDelinquency: z.boolean(),
  })
  .superRefine((value, context) => {
    if (!value.publishAgreement) return;
    const issue = (field: 'bodyFa' | 'titleFa', message: string) =>
      context.addIssue({ code: 'custom', message, path: ['agreement', field] });
    const title = value.agreement.titleFa.trim();
    const body = value.agreement.bodyFa.trim();
    if (title === '') issue('titleFa', 'عنوان توافق‌نامه را بنویسید.');
    else if ([...title].length > 200) issue('titleFa', 'حداکثر ۲۰۰ کاراکتر.');
    if (body === '') issue('bodyFa', 'متن توافق‌نامه را بنویسید.');
    else if ([...body].length > 20000) issue('bodyFa', 'حداکثر ۲۰۰۰۰ کاراکتر.');
  });

const DOCUMENT_CODE = /^[A-Z][A-Z0-9_]{1,31}$/;

const buyerProvisioningSchema = z.object({
  ...meta,
  acceptedIdentityDocumentTypes: z
    .array(z.string())
    .min(1, 'حداقل یک نوع مدرک.')
    .max(10, 'حداکثر ده نوع مدرک.')
    .refine((codes) => codes.every((code) => DOCUMENT_CODE.test(code)), 'کد نامعتبر است.')
    .refine((codes) => new Set(codes).size === codes.length, 'کد تکراری حذف شود.'),
  defaultInitialCreditIrr: requiredRials('اعتبار پایه پیش‌فرض را وارد کنید.'),
  sendLoginInvitationByDefault: z.boolean(),
});

const IDENTITY_DOCUMENT_OPTIONS = [
  { label: statusLabel('identityDocumentType', 'NATIONAL_CARD'), value: 'NATIONAL_CARD' },
];

const approvalSchema = z
  .object({
    ...meta,
    actions: z.array(
      z.object({
        actionType: z.string(),
        enabled: z.boolean(),
        expiresAfterHours: intField(1, 720),
        thresholdIrr: z.number().int().min(0).nullable(),
      }),
    ),
  })
  .superRefine((value, context) => {
    value.actions.forEach((action, index) => {
      if (
        !ALWAYS_APPROVAL_ACTIONS.has(action.actionType as ApprovalActionType) &&
        action.enabled &&
        action.thresholdIrr === null
      ) {
        context.addIssue({
          code: 'custom',
          message: 'آستانه را وارد کنید؛ صفر یعنی همیشه.',
          path: ['actions', index, 'thresholdIrr'],
        });
      }
    });
  });

/** API rate with six fractional digits (AC §4.2), or null when it is outside 0..10%. */
function canonicalPaymentFeeRate(value: string): string | null {
  const text = normalizeCodeInput(value).trim();
  if (!/^0(?:\.\d{1,6})?$/.test(text)) return null;
  const [whole, fraction = ''] = text.split('.');
  const rate = `${whole ?? '0'}.${fraction.padEnd(6, '0')}`;
  const numeric = Number(rate);
  if (numeric < 0 || numeric > 0.1) return null;
  return rate;
}

const paymentFeeSchema = z
  .object({
    ...meta,
    brackets: z
      .array(
        z.object({
          rate: z.string(),
          upToIrr: z.number().int().nullable(),
        }),
      )
      .min(1, 'دست‌کم یک بازه لازم است.')
      .max(10, 'حداکثر ده بازه.'),
    minimumFeeIrr: z.number().int().min(0).nullable(),
  })
  .superRefine((value, context) => {
    const minimum = value.minimumFeeIrr;
    if (minimum === null) {
      context.addIssue({
        code: 'custom',
        message: 'حداقل کارمزد را وارد کنید.',
        path: ['minimumFeeIrr'],
      });
    } else if (minimum > 1_000_000) {
      context.addIssue({
        code: 'custom',
        message: 'حداقل کارمزد حداکثر ۱۰۰٬۰۰۰ تومان است.',
        path: ['minimumFeeIrr'],
      });
    } else if (minimum % 10 !== 0) {
      context.addIssue({
        code: 'custom',
        message: 'حداقل کارمزد باید مضرب یک تومان باشد.',
        path: ['minimumFeeIrr'],
      });
    }
    let previous: number | null = null;
    value.brackets.forEach((bracket, index) => {
      const last = index === value.brackets.length - 1;
      if (last) {
        if (bracket.upToIrr !== null) {
          context.addIssue({
            code: 'custom',
            message: 'بازهٔ آخر سقف ندارد.',
            path: ['brackets', index, 'upToIrr'],
          });
        }
      } else if (bracket.upToIrr === null || bracket.upToIrr < 1) {
        context.addIssue({
          code: 'custom',
          message: 'سقف این بازه را وارد کنید.',
          path: ['brackets', index, 'upToIrr'],
        });
      } else if (previous !== null && bracket.upToIrr <= previous) {
        context.addIssue({
          code: 'custom',
          message: 'سقف هر بازه باید از سقف بازهٔ قبل بیشتر باشد.',
          path: ['brackets', index, 'upToIrr'],
        });
      }
      if (bracket.upToIrr !== null) previous = bracket.upToIrr;
      if (canonicalPaymentFeeRate(bracket.rate) === null) {
        context.addIssue({
          code: 'custom',
          message: 'نرخ باید بین ۰ و ۱۰ درصد باشد.',
          path: ['brackets', index, 'rate'],
        });
      }
    });
  });

const cashSaleFeeSchema = z.object({
  ...meta,
  defaultRate: z
    .string()
    .refine(
      (value) => canonicalPaymentFeeRate(value) !== null,
      'کارمزد باید بین صفر و ده درصد باشد.',
    ),
});

type Writable = StagePolicyType;

function MetaFields<F extends FieldValues>({
  earliest,
  form,
}: {
  readonly earliest: string;
  readonly form: UseFormReturn<F, unknown, FieldValues>;
}) {
  return (
    <>
      <Controller
        control={form.control}
        name={'effectiveFrom' as Path<F>}
        render={({ field, fieldState }) => (
          <AdminSolarDateField
            error={fieldState.error?.message}
            hint={`زودترین تاریخ مجاز: ${formatLocalDate(earliest, 'long')}؛ امروز یعنی بلافاصله.`}
            label="تاریخ شروع اعتبار"
            name={field.name}
            onBlur={field.onBlur}
            onChange={field.onChange}
            value={field.value as string}
          />
        )}
      />
      <TextArea
        error={form.getFieldState('reason' as Path<F>, form.formState).error?.message}
        label="دلیل تغییر"
        {...form.register('reason' as Path<F>)}
      />
    </>
  );
}

function NumberInput<F extends FieldValues>({
  form,
  hint,
  label,
  name,
}: {
  readonly form: UseFormReturn<F, unknown, FieldValues>;
  readonly hint?: string;
  readonly label: string;
  readonly name: string;
}) {
  return (
    <TextField
      error={form.getFieldState(name as Path<F>, form.formState).error?.message}
      inputMode="numeric"
      label={label}
      ltr
      {...(hint === undefined ? {} : { hint })}
      {...form.register(name as Path<F>)}
    />
  );
}

function initialValues(type: Writable, latest: PolicyVersion | undefined, earliest: string) {
  const base = { effectiveFrom: earliest, reason: '' };
  const text = (value: number | undefined, fallback: number) => toPersianDigits(value ?? fallback);
  if (type === 'installment-bounds') {
    const v = latest?.policyType === 'installment-bounds' ? latest : undefined;
    return {
      ...base,
      endOfSaleMonthMinLeadDays: text(v?.endOfSaleMonthMinLeadDays, 7),
      maxIntervalDays: text(v?.maxIntervalDays, 60),
      maxTotalDurationDays: text(v?.maxTotalDurationDays, 180),
      minIntervalDays: text(v?.minIntervalDays, 7),
    };
  }
  if (type === 'late-fee') {
    const v = latest?.policyType === 'late-fee' ? latest : undefined;
    return {
      ...base,
      enabledModels: v?.enabledModels ?? ['NONE'],
      legalSignoffReference: v?.legalSignoffReference ?? '',
    };
  }
  if (type === 'credit') {
    const v = latest?.policyType === 'credit' ? latest : undefined;
    return {
      ...base,
      maxBaseCreditIrr: v?.maxBaseCreditIrr ?? null,
      maxTotalCreditPerBuyerIrr: v?.maxTotalCreditPerBuyerIrr ?? null,
    };
  }
  if (type === 'guarantee') {
    const v = latest?.policyType === 'guarantee' ? latest : undefined;
    return {
      ...base,
      // A revision starts from the agreement in force (AC §18.10.1 returns its title and body).
      agreement: {
        bodyFa: v?.currentAgreement?.bodyFa ?? '',
        titleFa: v?.currentAgreement?.titleFa ?? '',
      },
      maxGuaranteedBuyersPerGuarantor: text(v?.maxGuaranteedBuyersPerGuarantor, 3),
      publishAgreement: (v?.currentAgreement ?? null) === null,
      referralCodeTtlDays: text(v?.referralCodeTtlDays, 7),
      requiredDirectGuarantorCount: text(v?.requiredDirectGuarantorCount, 0),
      restrictLayer1GuarantorsOnDelinquency: v?.restrictLayer1GuarantorsOnDelinquency ?? true,
    };
  }
  if (type === 'buyer-provisioning') {
    const v = latest?.policyType === 'buyer-provisioning' ? latest : undefined;
    return {
      ...base,
      acceptedIdentityDocumentTypes: v?.acceptedIdentityDocumentTypes ?? ['NATIONAL_CARD'],
      defaultInitialCreditIrr: v?.defaultInitialCreditIrr ?? null,
      sendLoginInvitationByDefault: v?.sendLoginInvitationByDefault ?? true,
    };
  }
  if (type === 'approval') {
    const v = latest?.policyType === 'approval' ? latest : undefined;
    return {
      ...base,
      // Every action type once, in the §5 order; a type missing from the latest version starts
      // as "always" with 72 hours (the migration 0009 default).
      actions: APPROVAL_ACTION_TYPES.map((actionType) => {
        const row = v?.actions.find((action) => action.actionType === actionType);
        return {
          actionType,
          enabled: row?.enabled ?? true,
          expiresAfterHours: toPersianDigits(row?.expiresAfterHours ?? 72),
          thresholdIrr: ALWAYS_APPROVAL_ACTIONS.has(actionType) ? null : (row?.thresholdIrr ?? 0),
        };
      }),
    };
  }
  if (type === 'payment-fee') {
    const v = latest?.policyType === 'payment-fee' ? latest : undefined;
    return {
      ...base,
      brackets: (
        v?.brackets ?? [
          { rate: '0.017000', upToIrr: 10_000_000 },
          { rate: '0.015000', upToIrr: 50_000_000 },
          { rate: '0.012000', upToIrr: null },
        ]
      ).map((bracket) => ({ rate: bracket.rate, upToIrr: bracket.upToIrr })),
      minimumFeeIrr: v?.minimumFeeIrr ?? 17_000,
    };
  }
  if (type === 'cash-sale-fee') {
    const v = latest?.policyType === 'cash-sale-fee' ? latest : undefined;
    return { ...base, defaultRate: v?.defaultRate ?? '0.003700' };
  }
  if (type === 'confirmation') {
    const v = latest?.policyType === 'confirmation' ? latest : undefined;
    return {
      ...base,
      qrTtlSeconds: text(v?.qrTtlSeconds, 120),
      reservationTtlSeconds: text(v?.reservationTtlSeconds, 300),
      returnRequestTtlSeconds: text(v?.returnRequestTtlSeconds, 86_400),
      smsCodeTtlSeconds: text(v?.smsCodeTtlSeconds, 300),
    };
  }
  const v = latest?.policyType === 'delinquency' ? latest : undefined;
  return {
    ...base,
    graceDays: text(v?.graceDays, 7),
    lateFee: {
      dailyAmountIrr: v?.lateFee.dailyAmountIrr ?? null,
      dailyCapIrr: v?.lateFee.dailyCapIrr ?? null,
      fixedAmountIrr: v?.lateFee.fixedAmountIrr ?? null,
      fixedPercentage: v?.lateFee.fixedPercentage ?? '',
      model: v?.lateFee.model ?? 'NONE',
    },
    restrictBuyerOnDelinquency: v?.restrictBuyerOnDelinquency ?? true,
    writeOffCandidateDays: text(v?.writeOffCandidateDays, 180),
  };
}

const SCHEMAS: Readonly<Record<Writable, z.ZodType>> = {
  approval: approvalSchema,
  'buyer-provisioning': buyerProvisioningSchema,
  'cash-sale-fee': cashSaleFeeSchema,
  confirmation: confirmationSchema,
  credit: creditSchema,
  delinquency: delinquencySchema,
  guarantee: guaranteeSchema,
  'installment-bounds': boundsSchema,
  'late-fee': lateFeeSchema,
  'payment-fee': paymentFeeSchema,
};

const TYPE_FIELDS: Record<Writable, readonly string[]> = {
  approval: [
    'actions',
    ...APPROVAL_ACTION_TYPES.flatMap((_, index) => [
      `actions.${index}.enabled`,
      `actions.${index}.thresholdIrr`,
      `actions.${index}.expiresAfterHours`,
    ]),
  ],
  'buyer-provisioning': [
    'defaultInitialCreditIrr',
    'acceptedIdentityDocumentTypes',
    'sendLoginInvitationByDefault',
  ],
  'cash-sale-fee': ['defaultRate'],
  credit: ['maxBaseCreditIrr', 'maxTotalCreditPerBuyerIrr'],
  guarantee: [
    'requiredDirectGuarantorCount',
    'maxGuaranteedBuyersPerGuarantor',
    'referralCodeTtlDays',
    'restrictLayer1GuarantorsOnDelinquency',
    'agreement.titleFa',
    'agreement.bodyFa',
  ],
  'late-fee': ['enabledModels', 'legalSignoffReference'],
  'payment-fee': [
    'minimumFeeIrr',
    'brackets',
    ...Array.from({ length: 10 }, (_, index) => [
      `brackets.${index}.upToIrr`,
      `brackets.${index}.rate`,
    ]).flat(),
  ],
  confirmation: [
    'reservationTtlSeconds',
    'smsCodeTtlSeconds',
    'qrTtlSeconds',
    'returnRequestTtlSeconds',
  ],
  delinquency: [
    'graceDays',
    'lateFee.model',
    'lateFee.fixedAmountIrr',
    'lateFee.fixedPercentage',
    'lateFee.dailyAmountIrr',
    'lateFee.dailyCapIrr',
    'restrictBuyerOnDelinquency',
    'writeOffCandidateDays',
  ],
  'installment-bounds': [
    'minIntervalDays',
    'maxIntervalDays',
    'maxTotalDurationDays',
    'endOfSaleMonthMinLeadDays',
  ],
};

/**
 * The request body (AC §17.5.3 request union): meta, the explicit `policyType` discriminator (equal
 * to the path), and the type's values; the other late-fee models' fields are `null`.
 */
export function toPolicyVersionCreate(
  type: Writable,
  values: Record<string, unknown>,
  expectedVersion: number,
): PolicyVersionCreate {
  const meta = {
    effectiveFrom: values.effectiveFrom as string,
    expectedVersion,
    reason: values.reason as string,
  };
  if (type === 'delinquency') {
    const lateFee = values.lateFee as {
      dailyAmountIrr: number | null;
      dailyCapIrr: number | null;
      fixedAmountIrr: number | null;
      fixedPercentage: string;
      model: LateFeeModel;
    };
    const body: DelinquencyPolicyVersionCreate = {
      ...meta,
      graceDays: values.graceDays as number,
      lateFee: {
        dailyAmountIrr: lateFee.model === 'DAILY_WITH_CAP' ? lateFee.dailyAmountIrr : null,
        dailyCapIrr: lateFee.model === 'DAILY_WITH_CAP' ? lateFee.dailyCapIrr : null,
        fixedAmountIrr: lateFee.model === 'FIXED_AMOUNT' ? lateFee.fixedAmountIrr : null,
        fixedPercentage:
          lateFee.model === 'FIXED_PERCENTAGE_OF_INSTALLMENT'
            ? normalizeCodeInput(lateFee.fixedPercentage)
            : null,
        model: lateFee.model,
      },
      policyType: 'delinquency',
      restrictBuyerOnDelinquency: values.restrictBuyerOnDelinquency as boolean,
      writeOffCandidateDays: values.writeOffCandidateDays as number,
    };
    return body;
  }
  if (type === 'late-fee') {
    const signoff = (values.legalSignoffReference as string).trim();
    return {
      ...meta,
      enabledModels: values.enabledModels as LateFeeModel[],
      legalSignoffReference: signoff === '' ? null : signoff,
      policyType: 'late-fee',
    };
  }
  if (type === 'credit') {
    return {
      ...meta,
      maxBaseCreditIrr: values.maxBaseCreditIrr as number,
      maxTotalCreditPerBuyerIrr: values.maxTotalCreditPerBuyerIrr as number,
      policyType: 'credit',
    };
  }
  if (type === 'guarantee') {
    const agreement = values.agreement as { bodyFa: string; titleFa: string };
    return {
      ...meta,
      // Line breaks are kept; only the ends are trimmed (plain text, no HTML).
      ...(values.publishAgreement === true
        ? { agreement: { bodyFa: agreement.bodyFa.trim(), titleFa: agreement.titleFa.trim() } }
        : {}),
      maxGuaranteedBuyersPerGuarantor: values.maxGuaranteedBuyersPerGuarantor as number,
      policyType: 'guarantee',
      referralCodeTtlDays: values.referralCodeTtlDays as number,
      requiredDirectGuarantorCount: values.requiredDirectGuarantorCount as number,
      restrictLayer1GuarantorsOnDelinquency:
        values.restrictLayer1GuarantorsOnDelinquency as boolean,
    };
  }
  if (type === 'buyer-provisioning') {
    return {
      ...meta,
      acceptedIdentityDocumentTypes: values.acceptedIdentityDocumentTypes as string[],
      defaultInitialCreditIrr: values.defaultInitialCreditIrr as number,
      policyType: 'buyer-provisioning',
      sendLoginInvitationByDefault: values.sendLoginInvitationByDefault as boolean,
    };
  }
  if (type === 'approval') {
    // "Always" rows go out enabled without a threshold (AC §18.10); the server rejects anything else.
    const actions = (values.actions as ApprovalPolicyAction[]).map((action) =>
      ALWAYS_APPROVAL_ACTIONS.has(action.actionType)
        ? { ...action, enabled: true, thresholdIrr: null }
        : { ...action, thresholdIrr: action.enabled ? action.thresholdIrr : null },
    );
    return { ...meta, actions, policyType: 'approval' };
  }
  if (type === 'payment-fee') {
    const brackets = values.brackets as { rate: string; upToIrr: number | null }[];
    return {
      ...meta,
      brackets: brackets.map((bracket, index) => ({
        rate: canonicalPaymentFeeRate(bracket.rate) ?? normalizeCodeInput(bracket.rate),
        upToIrr: index === brackets.length - 1 ? null : bracket.upToIrr,
      })),
      minimumFeeIrr: values.minimumFeeIrr as number,
      policyType: 'payment-fee',
    };
  }
  if (type === 'cash-sale-fee') {
    const rate = values.defaultRate as string;
    return {
      ...meta,
      defaultRate: canonicalPaymentFeeRate(rate) ?? normalizeCodeInput(rate),
      policyType: 'cash-sale-fee',
    };
  }
  if (type === 'confirmation') {
    const body: ConfirmationPolicyVersionCreate = {
      ...meta,
      policyType: 'confirmation',
      qrTtlSeconds: values.qrTtlSeconds as number,
      reservationTtlSeconds: values.reservationTtlSeconds as number,
      returnRequestTtlSeconds: values.returnRequestTtlSeconds as number,
      smsCodeTtlSeconds: values.smsCodeTtlSeconds as number,
    };
    return body;
  }
  const body: InstallmentBoundsPolicyVersionCreate = {
    ...meta,
    endOfSaleMonthMinLeadDays: values.endOfSaleMonthMinLeadDays as number,
    maxIntervalDays: values.maxIntervalDays as number,
    maxTotalDurationDays: values.maxTotalDurationDays as number,
    minIntervalDays: values.minIntervalDays as number,
    policyType: 'installment-bounds',
  };
  return body;
}

/**
 * The guarantee agreement (AC §18.10): the version in force, and an optional new version published
 * with this policy version at its `effectiveAt`. From then on new referral codes need its acceptance.
 */
function GuaranteeAgreementFields<F extends FieldValues>({
  current,
  form,
}: {
  readonly current: GuaranteeAgreementRef;
  readonly form: UseFormReturn<F, unknown, FieldValues>;
}) {
  const publish = form.watch('publishAgreement' as Path<F>) === true;
  const [editing, setEditing] = useState(false);
  const error = (name: string) =>
    form.getFieldState(name as Path<F>, form.formState).error?.message;
  const body = form.watch('agreement.bodyFa' as Path<F>) as string;
  const title = form.watch('agreement.titleFa' as Path<F>) as string;
  const titleError = error('agreement.titleFa');
  const bodyError = error('agreement.bodyFa');
  // The fields live in the text dialog: a refused submit opens it so the errors are visible.
  useEffect(() => {
    if (publish && (titleError !== undefined || bodyError !== undefined)) {
      setEditing(true);
    }
  }, [bodyError, publish, titleError]);
  return (
    <fieldset className="admin-fieldset">
      <legend>توافق‌نامهٔ ضمانت</legend>
      {current === null ? (
        <Banner tone="warning">توافق‌نامهٔ ضمانت هنوز منتشر نشده است.</Banner>
      ) : (
        <TextArea label="متن توافق‌نامه" readOnly rows={6} value={current.bodyFa} />
      )}
      <Controller
        control={form.control}
        name={'publishAgreement' as Path<F>}
        render={({ field }) => (
          <Switch
            checked={field.value === true}
            label="انتشار نسخهٔ جدید همراه این نسخه"
            onCheckedChange={field.onChange}
          />
        )}
      />
      {publish ? (
        <>
          {editing ? null : (
            <div className="admin-actions">
              <Button onClick={() => setEditing(true)} type="button" variant="secondary">
                ویرایش متن
              </Button>
            </div>
          )}
          <Dialog
            fullScreenOnCompact
            onOpenChange={setEditing}
            open={editing}
            size="wizard"
            title="متن توافق‌نامه"
          >
            <div className="admin-form">
              <TextField
                error={error('agreement.titleFa')}
                label="عنوان"
                maxLength={200}
                {...form.register('agreement.titleFa' as Path<F>)}
              />
              <TextArea
                error={error('agreement.bodyFa')}
                label="متن"
                maxLength={20000}
                rows={16}
                {...form.register('agreement.bodyFa' as Path<F>)}
              />
              <Button onClick={() => setEditing(false)} type="button">
                تأیید
              </Button>
            </div>
          </Dialog>
          {!editing && title.trim() !== '' ? (
            <TextArea label="پیش‌نمایش نسخهٔ جدید" readOnly rows={4} value={body} />
          ) : null}
        </>
      ) : null}
    </fieldset>
  );
}

function FormMoneyField<F extends FieldValues>({
  form,
  hint,
  label,
  name,
}: {
  readonly form: UseFormReturn<F, unknown, FieldValues>;
  readonly hint?: string;
  readonly label: string;
  readonly name: string;
}) {
  return (
    <Controller
      control={form.control}
      name={name as Path<F>}
      render={({ field, fieldState }) => (
        <MoneyField
          error={fieldState.error?.message}
          {...(hint === undefined ? {} : { hint })}
          label={label}
          onChangeRials={field.onChange}
          valueRials={field.value as number | null}
        />
      )}
    />
  );
}

function CountField<F extends FieldValues>({
  form,
  label,
  max,
  min,
  name,
  suffix,
}: {
  readonly form: UseFormReturn<F, unknown, FieldValues>;
  readonly label: string;
  readonly max: number;
  readonly min: number;
  readonly name: string;
  readonly suffix: string;
}) {
  return (
    <Controller
      control={form.control}
      name={name as Path<F>}
      render={({ field, fieldState }) => {
        const parsed =
          field.value === '' || field.value === undefined
            ? null
            : Number(normalizeCodeInput(String(field.value)));
        const value = parsed === null || Number.isNaN(parsed) ? null : parsed;
        return (
          <NumberField
            error={fieldState.error?.message}
            label={label}
            max={max}
            min={min}
            onChange={(next) => {
              field.onChange(next === null ? '' : String(next));
            }}
            suffix={suffix}
            value={value}
          />
        );
      }}
    />
  );
}

/** The late-fee models (AC §18.10): «بدون جریمه» stays enabled; a sign-off unlocks D-07 models. */
function LateFeeFields<F extends FieldValues>({
  form,
}: {
  readonly form: UseFormReturn<F, unknown, FieldValues>;
}) {
  return (
    <Controller
      control={form.control}
      name={'enabledModels' as Path<F>}
      render={({ field, fieldState }) => {
        const selected = (field.value ?? []) as LateFeeModel[];
        return (
          <fieldset className="admin-fieldset">
            <legend>مدل‌های مجاز جریمه دیرکرد</legend>
            <ChipRow>
              {LATE_FEE_MODELS.map((model) => {
                const active = selected.includes(model);
                return (
                  <FilterChip
                    disabled={model === 'NONE'}
                    key={model}
                    onToggle={() =>
                      field.onChange(
                        active
                          ? selected.filter((item) => item !== model)
                          : LATE_FEE_MODELS.filter(
                              (item) => item === model || selected.includes(item),
                            ),
                      )
                    }
                    selected={active}
                  >
                    {statusLabel('lateFeeModel', model)}
                  </FilterChip>
                );
              })}
            </ChipRow>
            {fieldState.error?.message === undefined ? null : (
              <span className="saay-field__error" role="alert">
                {fieldState.error.message}
              </span>
            )}
            <TextField
              error={
                form.getFieldState('legalSignoffReference' as Path<F>, form.formState).error
                  ?.message
              }
              label="مرجع تأیید حقوقی"
              maxLength={200}
              {...form.register('legalSignoffReference' as Path<F>)}
            />
          </fieldset>
        );
      }}
    />
  );
}

/**
 * The approval policy rows (AC §18.10): every action type once. "Always" actions are shown locked
 * (always with a second approver, no threshold); a "policy" action is enabled or not, with the amount
 * at or above which it needs approval (0 = always) and the request expiry in hours.
 */
type ApprovalThresholdMode = 'always' | 'from' | 'never';

function approvalThresholdMode(
  enabled: boolean,
  thresholdIrr: number | null,
): ApprovalThresholdMode {
  if (!enabled) {
    return 'never';
  }
  if (thresholdIrr === 0) {
    return 'always';
  }
  return 'from';
}

function ApprovalActionsFields<F extends FieldValues>({
  form,
}: {
  readonly form: UseFormReturn<F, unknown, FieldValues>;
}) {
  const alwaysRows = APPROVAL_ACTION_TYPES.filter((actionType) =>
    ALWAYS_APPROVAL_ACTIONS.has(actionType),
  );
  const policyRows = APPROVAL_ACTION_TYPES.filter(
    (actionType) => !ALWAYS_APPROVAL_ACTIONS.has(actionType),
  );

  /**
   * One row, three aligned columns: the action | «تأیید دوم» (the rule, with the amount under it for
   * «از مبلغ…») | «مهلت تصمیم». The action and the rule sit on the fields' control line.
   */
  function renderRow(actionType: ApprovalActionType, index: number, locked: boolean) {
    const base = `actions.${index}`;
    const enabled = form.watch(`${base}.enabled` as Path<F>) as boolean;
    const thresholdIrr = form.watch(`${base}.thresholdIrr` as Path<F>) as number | null;
    const mode = locked ? 'always' : approvalThresholdMode(enabled, thresholdIrr);
    const actionLabel = statusLabel('approvalActionType', actionType);
    const titleId = `approval-action-${actionType}`;
    return (
      <div aria-labelledby={titleId} className="admin-approval-row" key={actionType} role="group">
        <strong className="admin-approval-row__action" id={titleId}>
          {actionLabel}
        </strong>
        <div className="admin-approval-row__rule">
          <span aria-hidden="true" className="admin-approval-row__label">
            تأیید دوم
          </span>
          {locked ? (
            <span className="admin-approval-row__fixed">
              <Icon name="lock" />
              همیشه لازم است
            </span>
          ) : (
            <SegmentedButtons
              label={`تأیید دوم برای ${actionLabel}`}
              onChange={(key) => {
                const next = key as ApprovalThresholdMode;
                if (next === 'never') {
                  form.setValue(`${base}.enabled` as Path<F>, false as never, {
                    shouldValidate: true,
                  });
                  form.setValue(`${base}.thresholdIrr` as Path<F>, 0 as never, {
                    shouldValidate: true,
                  });
                  return;
                }
                form.setValue(`${base}.enabled` as Path<F>, true as never, {
                  shouldValidate: true,
                });
                if (next === 'always') {
                  form.setValue(`${base}.thresholdIrr` as Path<F>, 0 as never, {
                    shouldValidate: true,
                  });
                }
              }}
              options={[
                { key: 'always', label: 'همیشه' },
                { key: 'from', label: 'از مبلغ…' },
                { key: 'never', label: 'هرگز' },
              ]}
              value={mode}
            />
          )}
          {locked || mode !== 'from' ? null : (
            <Controller
              control={form.control}
              name={`${base}.thresholdIrr` as Path<F>}
              render={({ field, fieldState }) => (
                <MoneyField
                  error={fieldState.error?.message}
                  label="از مبلغ"
                  onChangeRials={field.onChange}
                  required
                  valueRials={field.value as number | null}
                />
              )}
            />
          )}
        </div>
        <div className="admin-approval-row__deadline">
          <CountField
            form={form}
            label="مهلت تصمیم"
            max={720}
            min={1}
            name={`${base}.expiresAfterHours`}
            suffix="ساعت"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="admin-approval-policy" role="group" aria-label="قواعد تأیید دومرحله‌ای">
      <p className="admin-muted">
        هر اقدام این فهرست پیش از اجرا می‌تواند به تأیید مدیر دوم نیاز داشته باشد. «مهلت تصمیم»
        زمانی است که مدیر دوم برای تأیید یا رد دارد؛ پس از آن مهلت درخواست تمام می‌شود.
      </p>
      <section aria-labelledby="approval-always-heading" className="admin-approval-policy__group">
        <header className="admin-approval-policy__head">
          <h3 id="approval-always-heading">همیشه با تأیید دوم</h3>
          <p>
            این اقدام‌ها همیشه تأیید مدیر دوم را لازم دارند و این قاعده تغییر نمی‌کند؛ فقط مهلت
            تصمیم را تعیین کنید.
          </p>
        </header>
        {alwaysRows.map((actionType) =>
          renderRow(actionType, APPROVAL_ACTION_TYPES.indexOf(actionType), true),
        )}
      </section>
      <section aria-labelledby="approval-policy-heading" className="admin-approval-policy__group">
        <header className="admin-approval-policy__head">
          <h3 id="approval-policy-heading">قابل تنظیم</h3>
          <p>
            «همیشه»: هر درخواست تأیید دوم لازم دارد. «از مبلغ…»: فقط وقتی مبلغ برابر یا بیشتر از
            آستانه باشد. «هرگز»: بدون تأیید دوم اجرا می‌شود.
          </p>
        </header>
        {policyRows.map((actionType) =>
          renderRow(actionType, APPROVAL_ACTION_TYPES.indexOf(actionType), false),
        )}
      </section>
    </div>
  );
}

/**
 * The payment-fee schedule (AC §25.3, FTD §9.13): brackets of an upper bound in toman plus a flat
 * rate, the last bracket open, and a minimum fee. Only Super Admin publishes a version.
 */
function PaymentFeeFields<F extends FieldValues>({
  form,
}: {
  readonly form: UseFormReturn<F, unknown, FieldValues>;
}) {
  const rows = useFieldArray({ control: form.control as never, name: 'brackets' });
  return (
    <fieldset className="admin-fieldset">
      <legend>بازه‌های کارمزد پرداخت</legend>
      <p className="admin-muted">
        نرخ روی کل مبلغ پایهٔ همان بازه اعمال می‌شود. بازهٔ آخر سقف ندارد. حداقل کارمزد برای هر
        پرداخت جدا از نرخ است.
      </p>
      {rows.fields.map((row, index) => {
        const open = index === rows.fields.length - 1;
        return (
          <div className="admin-grid-2" key={row.id}>
            {open ? (
              <TextField label="سقف بازه" readOnly value="بدون سقف" />
            ) : (
              <Controller
                control={form.control}
                name={`brackets.${index}.upToIrr` as Path<F>}
                render={({ field, fieldState }) => (
                  <MoneyField
                    error={fieldState.error?.message}
                    label="سقف بازه"
                    onChangeRials={field.onChange}
                    valueRials={field.value as number | null}
                  />
                )}
              />
            )}
            <Controller
              control={form.control}
              name={`brackets.${index}.rate` as Path<F>}
              render={({ field, fieldState }) => (
                <PercentField
                  error={fieldState.error?.message}
                  label="نرخ"
                  onBlur={field.onBlur}
                  onChangeRate={(rate) => {
                    field.onChange(rate ?? '');
                  }}
                  valueRate={field.value === '' ? null : (field.value as string)}
                />
              )}
            />
            {open ? null : (
              <Button onClick={() => rows.remove(index)} type="button" variant="secondary">
                حذف بازه
              </Button>
            )}
          </div>
        );
      })}
      <div className="admin-actions">
        <Button
          disabled={rows.fields.length >= 10}
          onClick={() =>
            rows.insert(Math.max(0, rows.fields.length - 1), {
              rate: '0.015000',
              upToIrr: null,
            } as never)
          }
          type="button"
          variant="secondary"
        >
          افزودن بازه
        </Button>
      </div>
      <FormMoneyField
        form={form}
        hint="صفر یعنی بدون حداقل. حداکثر ۱۰۰٬۰۰۰ تومان."
        label="حداقل کارمزد"
        name="minimumFeeIrr"
      />
    </fieldset>
  );
}

/** AC §34.4: one rate, shown and typed as a percent; a store's own rate stays where it is set. */
function CashSaleFeeFields<F extends FieldValues>({
  form,
}: {
  readonly form: UseFormReturn<F, unknown, FieldValues>;
}) {
  return (
    <Controller
      control={form.control}
      name={'defaultRate' as Path<F>}
      render={({ field, fieldState }) => (
        <PercentField
          error={fieldState.error?.message}
          hint="برای فروشگاهی که نرخ خودش را ندارد. حداکثر ۱۰ درصد."
          label="کارمزد پیش‌فرض فروش نقدی"
          onBlur={field.onBlur}
          onChangeRate={(rate) => {
            field.onChange(rate ?? '');
          }}
          valueRate={field.value === '' ? null : (field.value as string)}
        />
      )}
    />
  );
}

function NewVersionForm({
  enabledModels,
  latest,
  onDone,
  type,
}: {
  readonly enabledModels: readonly LateFeeModel[];
  readonly latest: PolicyVersion | undefined;
  /** The server confirmed the new version (the editor sheet closes). */
  readonly onDone: () => void;
  readonly type: Writable;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const offline = useSubmitBlocked();
  const today = tehranToday();
  const earliest =
    latest !== undefined && latest.effectiveFrom > today ? latest.effectiveFrom : today;
  const expectedVersion = latest?.version ?? 0;
  const [unmapped, setUnmapped] = useState<ReturnType<typeof applyFieldErrors>>([]);
  const [conflict, setConflict] = useState(false);
  const form = useForm<FieldValues, unknown, FieldValues>({
    defaultValues: initialValues(type, latest, earliest),
    resolver: zodResolver(SCHEMAS[type] as never),
  });
  const command = useCommand<PolicyVersionCreate, PolicyVersion>(
    `policy.${type}.create`,
    (client, body, intent) => client.post(`/admin/policies/${type}/versions`, { body, intent }),
    {
      onSuccess: async (result) => {
        await queryClient.invalidateQueries({ queryKey: setupKeys.policy(type) });
        toast.success({
          confirmedBy: result,
          description:
            result.data.status === 'SCHEDULED'
              ? `از ${formatLocalDate(result.data.effectiveFrom, 'long')} اعمال می‌شود.`
              : type === 'payment-fee'
                ? 'از همین لحظه برای پرداخت‌های بعدی اعمال شد. پرداخت‌های شروع‌شده تغییر نمی‌کنند.'
                : type === 'cash-sale-fee'
                  ? 'از همین لحظه برای فروش‌های نقدی بعدی اعمال شد. فروش‌های ثبت‌شده تغییر نمی‌کنند.'
                  : 'از همین لحظه اعمال شد. قراردادهای تأییدشده تغییر نمی‌کنند.',
          title: `نسخه ${toPersianDigits(result.data.version)} ثبت شد`,
        });
        onDone();
      },
    },
  );

  function handle(outcome: CommandOutcome<PolicyVersion>) {
    if (outcome.status !== 'error' || !isApiError(outcome.error)) return;
    const error = outcome.error;
    setUnmapped(
      applyFieldErrors(error, ['effectiveFrom', 'reason', ...TYPE_FIELDS[type]], form.setError),
    );
    if (error.code === 'RESOURCE_VERSION_CONFLICT') {
      setConflict(true);
      void queryClient.invalidateQueries({ queryKey: setupKeys.policy(type) });
    }
    if (error.code === 'POLICY_EFFECTIVE_FROM_INVALID') {
      const earliestAllowed = error.details?.earliestEffectiveFrom;
      form.setError('effectiveFrom', {
        message:
          typeof earliestAllowed === 'string'
            ? `${error.messageFa} زودترین تاریخ: ${formatLocalDate(earliestAllowed, 'long')}.`
            : error.messageFa,
        type: 'server',
      });
    }
    if (error.code === 'LATE_FEE_MODEL_NOT_ENABLED') {
      form.setError('lateFee.model', { message: error.messageFa, type: 'server' });
    }
    if (error.code === 'LATE_FEE_MODEL_IN_USE') {
      const model = error.details?.model;
      form.setError('enabledModels', {
        message:
          typeof model === 'string'
            ? `${error.messageFa} (${statusLabel('lateFeeModel', model)})`
            : error.messageFa,
        type: 'server',
      });
    }
    if (error.code === 'CREDIT_POLICY_LIMIT_EXCEEDED') {
      const limit = error.details?.limitIrr;
      form.setError('defaultInitialCreditIrr', {
        message:
          typeof limit === 'number'
            ? `${error.messageFa} سقف اعتبار پایه: ${formatToman(limit)}.`
            : error.messageFa,
        type: 'server',
      });
    }
  }

  async function submit(values: FieldValues) {
    setUnmapped([]);
    setConflict(false);
    handle(await command.submit(toPolicyVersionCreate(type, values, expectedVersion)));
  }

  const model = form.watch('lateFee.model') as LateFeeModel | undefined;
  const handledCodes = new Set([
    'RESOURCE_VERSION_CONFLICT',
    'POLICY_EFFECTIVE_FROM_INVALID',
    'LATE_FEE_MODEL_NOT_ENABLED',
    'LATE_FEE_MODEL_IN_USE',
    'CREDIT_POLICY_LIMIT_EXCEEDED',
    'REQUEST_INVALID',
  ]);
  const feedbackState =
    command.state.status === 'error' &&
    isApiError(command.state.error) &&
    handledCodes.has(command.state.error.code)
      ? { ...command.state, status: 'idle' as const }
      : command.state;

  return (
    <form
      aria-label="نسخه جدید سیاست"
      className="admin-form"
      key={expectedVersion}
      noValidate
      onSubmit={form.handleSubmit(submit)}
    >
      <p className="admin-muted">
        {type === 'payment-fee'
          ? `نسخه جدید روی آخرین نسخه (${toPersianDigits(expectedVersion)}) ساخته می‌شود. کارمزد هنگام شروع پرداخت از نسخهٔ جاری محاسبه می‌شود و پرداخت‌های شروع‌شده تغییر نمی‌کنند.`
          : type === 'cash-sale-fee'
            ? `نسخه جدید روی آخرین نسخه (${toPersianDigits(expectedVersion)}) ساخته می‌شود. فقط فروش‌های نقدی بعد از شروع این نسخه از آن استفاده می‌کنند. فروش‌های ثبت‌شده و نرخ خود فروشگاه‌ها تغییر نمی‌کنند.`
            : `نسخه جدید روی آخرین نسخه (${toPersianDigits(expectedVersion)}) ساخته می‌شود. قراردادهای تأییدشده تغییر نمی‌کنند.`}
      </p>
      {type === 'installment-bounds' ? (
        <div className="admin-grid-2">
          <CountField
            form={form}
            label="حداقل فاصله اقساط"
            max={366}
            min={1}
            name="minIntervalDays"
            suffix="روز"
          />
          <CountField
            form={form}
            label="حداکثر فاصله اقساط"
            max={366}
            min={1}
            name="maxIntervalDays"
            suffix="روز"
          />
          <CountField
            form={form}
            label="حداکثر مدت کل"
            max={3660}
            min={1}
            name="maxTotalDurationDays"
            suffix="روز"
          />
          <CountField
            form={form}
            label="حداقل فاصله تا پایان ماه"
            max={29}
            min={0}
            name="endOfSaleMonthMinLeadDays"
            suffix="روز"
          />
        </div>
      ) : null}
      {type === 'confirmation' ? (
        <div className="admin-grid-2">
          <CountField
            form={form}
            label="مهلت رزرو"
            max={1800}
            min={60}
            name="reservationTtlSeconds"
            suffix="ثانیه"
          />
          <CountField
            form={form}
            label="اعتبار کد پیامکی"
            max={1800}
            min={60}
            name="smsCodeTtlSeconds"
            suffix="ثانیه"
          />
          <CountField
            form={form}
            label="اعتبار QR"
            max={600}
            min={30}
            name="qrTtlSeconds"
            suffix="ثانیه"
          />
          <CountField
            form={form}
            label="مهلت تأیید مرجوعی"
            max={604_800}
            min={60}
            name="returnRequestTtlSeconds"
            suffix="ثانیه"
          />
        </div>
      ) : null}
      {type === 'delinquency' ? (
        <>
          <div className="admin-grid-2">
            <CountField
              form={form}
              label="مهلت پرداخت پس از سررسید"
              max={60}
              min={0}
              name="graceDays"
              suffix="روز"
            />
            <CountField
              form={form}
              label="نامزد سوخت پس از"
              max={3650}
              min={1}
              name="writeOffCandidateDays"
              suffix="روز"
            />
          </div>
          <NativeSelect
            error={form.getFieldState('lateFee.model', form.formState).error?.message}
            hint="فقط مدل‌های فعال در سیاست «مدل‌های مجاز جریمه دیرکرد» قابل انتخاب‌اند."
            id="policy-late-fee-model"
            label="مدل جریمه دیرکرد"
            options={enabledModels.map((value) => ({
              label: statusLabel('lateFeeModel', value),
              value,
            }))}
            {...form.register('lateFee.model')}
          />
          {model === 'FIXED_AMOUNT' ? (
            <Controller
              control={form.control}
              name="lateFee.fixedAmountIrr"
              render={({ field, fieldState }) => (
                <MoneyField
                  error={fieldState.error?.message}
                  label="مبلغ ثابت جریمه"
                  onChangeRials={field.onChange}
                  valueRials={field.value as number | null}
                />
              )}
            />
          ) : null}
          {model === 'FIXED_PERCENTAGE_OF_INSTALLMENT' ? (
            <Controller
              control={form.control}
              name="lateFee.fixedPercentage"
              render={({ field, fieldState }) => (
                <PercentField
                  error={fieldState.error?.message}
                  label="نرخ جریمه از قسط"
                  onBlur={field.onBlur}
                  onChangeRate={(rate) => {
                    field.onChange(rate ?? '');
                  }}
                  valueRate={field.value === '' ? null : field.value}
                />
              )}
            />
          ) : null}
          {model === 'DAILY_WITH_CAP' ? (
            <div className="admin-grid-2">
              <Controller
                control={form.control}
                name="lateFee.dailyAmountIrr"
                render={({ field, fieldState }) => (
                  <MoneyField
                    error={fieldState.error?.message}
                    label="جریمه روزانه"
                    onChangeRials={field.onChange}
                    valueRials={field.value as number | null}
                  />
                )}
              />
              <Controller
                control={form.control}
                name="lateFee.dailyCapIrr"
                render={({ field, fieldState }) => (
                  <MoneyField
                    error={fieldState.error?.message}
                    label="سقف جریمه"
                    onChangeRials={field.onChange}
                    valueRials={field.value as number | null}
                  />
                )}
              />
            </div>
          ) : null}
          <Controller
            control={form.control}
            name="restrictBuyerOnDelinquency"
            render={({ field }) => (
              <Switch
                checked={field.value === true}
                label="خریدار معوق از خرید محدود شود"
                onCheckedChange={field.onChange}
              />
            )}
          />
        </>
      ) : null}
      {type === 'late-fee' ? <LateFeeFields form={form} /> : null}
      {type === 'credit' ? (
        <div className="admin-grid-2">
          <FormMoneyField form={form} label="سقف اعتبار پایه" name="maxBaseCreditIrr" />
          <FormMoneyField
            form={form}
            hint="مجموع سقف تخصیص‌های فعال و زمان‌بندی‌شدهٔ هر خریدار"
            label="سقف کل اعتبار هر خریدار"
            name="maxTotalCreditPerBuyerIrr"
          />
        </div>
      ) : null}
      {type === 'guarantee' ? (
        <>
          <div className="admin-grid-2">
            <NumberInput
              form={form}
              hint="صفر یعنی خرید بدون ضامن"
              label="تعداد ضامن مستقیم لازم"
              name="requiredDirectGuarantorCount"
            />
            <NumberInput
              form={form}
              label="حداکثر خریدار تحت ضمانت هر ضامن"
              name="maxGuaranteedBuyersPerGuarantor"
            />
            <NumberInput form={form} label="اعتبار کد معرف (روز)" name="referralCodeTtlDays" />
          </div>
          <Controller
            control={form.control}
            name="restrictLayer1GuarantorsOnDelinquency"
            render={({ field }) => (
              <Switch
                checked={field.value === true}
                label="ضامنان لایهٔ اول خریدار معوق محدود شوند"
                onCheckedChange={field.onChange}
              />
            )}
          />
          <p className="admin-muted">
            <OnDemandCopy details={GUARANTEE_POLICY_FULL_FA} short={GUARANTEE_POLICY_SHORT_FA} />
          </p>
          <GuaranteeAgreementFields
            current={latest?.policyType === 'guarantee' ? latest.currentAgreement : null}
            form={form}
          />
        </>
      ) : null}
      {type === 'buyer-provisioning' ? (
        <>
          <div className="admin-grid-2">
            <FormMoneyField
              form={form}
              hint="از سقف اعتبار پایهٔ سیاست اعتبار بیشتر نباشد."
              label="اعتبار پایه پیش‌فرض"
              name="defaultInitialCreditIrr"
            />
            <Controller
              control={form.control}
              name="acceptedIdentityDocumentTypes"
              render={({ field, fieldState }) => (
                <MultiSelectCombobox
                  error={fieldState.error?.message}
                  label="مدارک شناسایی"
                  onChange={field.onChange}
                  options={IDENTITY_DOCUMENT_OPTIONS}
                  value={field.value as string[]}
                />
              )}
            />
          </div>
          <Controller
            control={form.control}
            name="sendLoginInvitationByDefault"
            render={({ field }) => (
              <Switch
                checked={field.value === true}
                label="پیامک دعوت به‌صورت پیش‌فرض"
                onCheckedChange={field.onChange}
              />
            )}
          />
        </>
      ) : null}
      {type === 'approval' ? <ApprovalActionsFields form={form} /> : null}
      {type === 'payment-fee' ? <PaymentFeeFields form={form} /> : null}
      {type === 'cash-sale-fee' ? <CashSaleFeeFields form={form} /> : null}
      <MetaFields earliest={earliest} form={form} />
      {conflict ? (
        <Banner tone="warning">
          در این فاصله نسخه جدیدی ثبت شده است. فهرست به‌روز شد؛ مقادیر را بازبینی و دوباره ثبت کنید.
        </Banner>
      ) : null}
      <FieldErrorList errors={unmapped} />
      <CommandFeedback
        onCheckStatus={() => void command.checkStatus().then(handle)}
        state={feedbackState}
      />
      <div className="admin-actions admin-actions--end admin-policy-form__actions">
        <Button
          disabled={offline.blocked}
          loading={command.state.status === 'pending'}
          type="submit"
        >
          ثبت نسخه جدید
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------------------------
// The page

function PolicyPanel({ self, type }: { readonly self: AdminSelf; readonly type: StagePolicyType }) {
  const current = useCurrentPolicy(type);
  const versions = usePolicyVersions(type);
  const manage = canManagePolicy(self, type);
  const lateFee = useCurrentPolicy<LateFeePolicyVersion>(
    'late-fee',
    manage && type === 'delinquency',
  );
  const rows = versions.data?.pages.flatMap((page) => page.items) ?? [];
  const latest = [...rows].sort((left, right) => right.version - left.version)[0];
  const scheduled = rows.find((row) => row.status === 'SCHEDULED');
  const noCurrent = isApiError(current.error) && current.error.status === 404;
  const [editOpen, setEditOpen] = useState(false);

  return (
    <div className="admin-sections">
      <Section id={`policy-${type}-current`} title="نسخه جاری">
        {noCurrent && versions.status === 'success' && rows.length === 0 ? (
          // Credit has no seed (Q-S3-03): until a version exists the credit caps are not checked.
          <EmptyState
            action={
              manage ? (
                <Button onClick={() => setEditOpen(true)} variant="primary">
                  تعریف اولین نسخه
                </Button>
              ) : undefined
            }
            description={
              type === 'credit'
                ? 'تا تعریف این سیاست، سقف اعتبار پایه و سقف کل اعتبار هر خریدار بررسی نمی‌شود.'
                : 'هنوز نسخه‌ای از این سیاست ثبت نشده است.'
            }
            title="تعریف نشده"
          />
        ) : noCurrent ? (
          <EmptyState
            action={
              manage ? (
                <Button onClick={() => setEditOpen(true)} variant="primary">
                  نسخه جدید
                </Button>
              ) : undefined
            }
            title="فعلاً هیچ نسخه‌ای جاری نیست"
          />
        ) : (
          <QueryState
            error={current.error}
            onRetry={() => void current.refetch()}
            skeleton={<Skeleton label="در حال بارگذاری نسخه جاری…" />}
            status={current.status}
          >
            {current.data === undefined ? null : (
              <CurrentVersion
                manage={manage}
                onEdit={() => setEditOpen(true)}
                scheduled={scheduled}
                version={current.data}
              />
            )}
          </QueryState>
        )}
      </Section>
      {manage ? (
        <SideSheet
          // Phones get the whole screen (MD3 full-screen dialog); the title and «بستن» stay pinned.
          fullScreenOnCompact
          onOpenChange={setEditOpen}
          open={editOpen}
          // The approval rows need three aligned columns (action, rule, deadline).
          size={type === 'approval' ? 'wizard' : 'default'}
          stickyHeader
          title={rows.length === 0 ? 'اولین نسخه' : 'نسخه جدید'}
        >
          {versions.status === 'success' &&
          (type !== 'delinquency' || lateFee.status === 'success') ? (
            <NewVersionForm
              enabledModels={lateFee.data?.enabledModels ?? ['NONE']}
              key={`${type}:${latest?.version ?? 0}`}
              latest={latest}
              onDone={() => setEditOpen(false)}
              type={type}
            />
          ) : type === 'delinquency' && lateFee.status === 'error' ? (
            <ErrorState error={lateFee.error} onRetry={() => void lateFee.refetch()} />
          ) : (
            <Skeleton label="در حال آماده‌سازی فرم…" />
          )}
        </SideSheet>
      ) : null}
      <Section id={`policy-${type}-history`} title="سابقه نسخه‌ها">
        <QueryState
          empty={<EmptyState title="نسخه‌ای ثبت نشده است" />}
          error={versions.error}
          isEmpty={rows.length === 0}
          onRetry={() => void versions.refetch()}
          skeleton={<Skeleton label="در حال بارگذاری سابقه…" lines={4} />}
          status={listStatus(versions)}
        >
          <History versions={rows} />
          <LoadMore
            hasMore={versions.hasNextPage}
            loading={versions.isFetchingNextPage}
            onLoadMore={() => void versions.fetchNextPage()}
          />
        </QueryState>
      </Section>
    </div>
  );
}

type AnyPolicyTab = StagePolicyType | WalletPolicyType | NumberingPolicyType;

function isWalletPolicyType(type: AnyPolicyTab): type is WalletPolicyType {
  return (WALLET_POLICY_TYPES as readonly string[]).includes(type);
}

/**
 * The card numbering moved to «کارت‌های فیزیکی» (D-117): one line and a link, so there is a single
 * editor. The list item stays so a reader who looks here still finds it.
 */
function NumberingPointer() {
  return (
    <section aria-label="کارت فیزیکی">
      <Notice
        action={
          <Link
            className={cn(buttonVariants({ variant: 'outline' }), OUTLINE_PILL)}
            to={physicalCardsPath('settings')}
          >
            رفتن به کارت‌های فیزیکی
          </Link>
        }
        icon={CreditCardIcon}
        title="تنظیمات کارت فیزیکی به صفحهٔ کارت‌های فیزیکی منتقل شد"
        tone="forest"
      />
    </section>
  );
}

export function PoliciesPage() {
  usePageTitle('سیاست‌ها');
  const [params, setParams] = useSearchParams();
  return (
    <RequireArea area="policies">
      {(self) => {
        const readable: AnyPolicyTab[] = [
          ...POLICY_TYPES.filter((type) => canReadPolicy(self, type)),
          ...WALLET_POLICY_TYPES.filter((type) => canReadWalletPolicy(self, type)),
          ...(canReadNumberingPolicy(self) ? [NUMBERING_POLICY_TYPE] : []),
        ];
        const requested = params.get('type');
        const type =
          readable.find((candidate) => candidate === requested) ??
          readable[0] ??
          'installment-bounds';
        return (
          <>
            <PageHeader title="سیاست‌ها" />
            <div className="admin-policy-layout">
              <List label="نوع سیاست">
                {readable.map((key) => (
                  <ListItem
                    as="button"
                    key={key}
                    onClick={() => setParams({ type: key })}
                    primary={statusLabel('policyType', key)}
                    selected={key === type}
                  />
                ))}
              </List>
              {type === NUMBERING_POLICY_TYPE ? (
                <NumberingPointer key={type} />
              ) : isWalletPolicyType(type) ? (
                <WalletPolicyPanel key={type} self={self} type={type} />
              ) : (
                <PolicyPanel key={type} self={self} type={type} />
              )}
            </div>
          </>
        );
      }}
    </RequireArea>
  );
}
